import { getApiErrorMessage } from "../../../helpers/aws_api_helper";

export const SCHEDULE_STORAGE_KEY = "cspmConfigurationSchedule";
export const DEFAULT_SUPPRESS_REASON = "Suppressed via UI";
export const DEFAULT_REMOVE_REASON = "Removed via UI";
export const SEVERITY_OPTIONS = ["CRITICAL", "HIGH", "MEDIUM", "LOW", "INFORMATIONAL"];

const SEVERITY_STYLES = {
  CRITICAL: { backgroundColor: "#fee2e2", color: "#b91c1c" },
  HIGH: { backgroundColor: "#ffedd5", color: "#c2410c" },
  MEDIUM: { backgroundColor: "#fef9c3", color: "#a16207" },
  LOW: { backgroundColor: "#dcfce7", color: "#15803d" },
  INFORMATIONAL: { backgroundColor: "#e0f2fe", color: "#0369a1" },
};

export function readPagedList(response, fallback) {
  if (!response || response.status !== 200) {
    throw new Error(getApiErrorMessage(response) || fallback);
  }
  const items = Array.isArray(response.items) ? response.items : [];
  const totalNumber = Number(response.total);
  return {
    items,
    page: Number.isFinite(Number(response.page)) ? Number(response.page) : 1,
    pageSize: Number.isFinite(Number(response.page_size)) ? Number(response.page_size) : items.length,
    total: Number.isFinite(totalNumber) ? totalNumber : items.length,
  };
}

export function readMutationMessage(response) {
  const status = response?.status;
  if (typeof status === "number" && status >= 200 && status < 300) {
    const message = response?.data?.message || response?.message;
    if (typeof message === "string" && message.trim()) return message.trim();
    return "Check suppressions processed";
  }
  throw new Error(getApiErrorMessage(response) || "Unable to update check suppressions.");
}

export function formatLabel(value) {
  if (value === null || value === undefined || String(value).trim() === "") return "—";
  return String(value)
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function cspmProviderParam(selectedProvider) {
  const value = String(selectedProvider || "").trim().toLowerCase();
  if (value === "aws" || value === "azure") return value;
  return "";
}

export function severityStyle(severity) {
  return SEVERITY_STYLES[String(severity || "").toUpperCase()] || {
    backgroundColor: "#f1f5f9",
    color: "#475569",
  };
}

export function readStoredPageSize(key, fallback, allowed) {
  try {
    const stored = Number(localStorage.getItem(key));
    if (allowed.includes(stored)) return stored;
  } catch (storageError) {
    return fallback;
  }
  return fallback;
}

export function storePageSize(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch (storageError) {
    return undefined;
  }
  return undefined;
}

export function readStoredSchedule(scheduleId) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SCHEDULE_STORAGE_KEY) || "null");
    if (stored && stored.schedule_id === scheduleId) return stored;
  } catch (error) {
    return null;
  }
  return null;
}

export function readFlag(value) {
  if (value === true || value === "true") return true;
  if (value === false || value === "false") return false;
  return null;
}

export function baselineFromCheck(check) {
  const suppressed = readFlag(check?.is_suppressed) === true;
  return {
    suppressed,
    reason: check?.reason || "",
    wasSuppressed: suppressed,
    originalReason: check?.reason || "",
  };
}

export function resolveCheckState(check, pending) {
  if (check?.check_id && pending?.has(check.check_id)) {
    return pending.get(check.check_id);
  }
  return baselineFromCheck(check);
}

export function normalizedReason(entry) {
  const reason = String(entry?.reason || "").trim();
  return reason || DEFAULT_SUPPRESS_REASON;
}

export function isCheckChange(entry) {
  if (!entry) return false;
  if (Boolean(entry.suppressed) !== Boolean(entry.wasSuppressed)) return true;
  if (!entry.suppressed || !entry.wasSuppressed) return false;
  const original = String(entry.originalReason || "").trim() || DEFAULT_SUPPRESS_REASON;
  return normalizedReason(entry) !== original;
}

export function countCheckChanges(pending) {
  let count = 0;
  pending.forEach((entry) => {
    if (isCheckChange(entry)) count += 1;
  });
  return count;
}
